import { uniq } from "lodash-es"
import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import type { SegmentResponse } from "@/types/api"

enum SegmentVariant {
  DEVICE = "device",
  GEOLOCATION = "geolocation",
  NETWORK = "network",
  PROTOCOL = "protocol",
  ROOT = "root"
}

export type SegmentType = `${SegmentVariant}`

export enum SegmentBalancingVariant {
  PERCENTAGE = "percentage",
  CAPACITY = "capacity"
}

export type SegmentBalancingModeType = `${SegmentBalancingVariant}`

export enum SegmentMode {
  NORMAL = "normal",
  SIBLING = "sibling"
}
type SegmentModeType = `${SegmentMode}`

export interface CdnEditFormType {
  cdnName: string
  pathwayId: string
}

interface BalanceMap<T> {
  [cdnUuid: string]: T
}

interface PartialSegment {
  mode?: SegmentModeType
  balancings?: BalanceMap<number>
  devices?: NonNullable<api.switchns.PatchedSegmentRequest["devices"]>
  geolocation?: api.switchns.Geolocation | null
  name?: string | null
  parentUuid?: string | null
  protocol?: api.switchns.PatchedSegmentRequest["protocol"]
  segmentType?: SegmentResponse["type"] | SegmentType
  segmentBalancingMode?: SegmentResponse["balancingMode"] | SegmentBalancingModeType
  capacityBalancingEnabled?: boolean
  segmentUuid?: string | null
  siblingsCountryCodes?: api.switchns.Geolocation["countries"]
  siblingsSubdivisionCodes?: api.switchns.Geolocation["subdivisions"]
  asns?: api.switchns.Asn[]
}

interface WizardMethods {
  clearWizard: () => void
  setSegmentWizardState: (newState: PartialSegment) => void
  setSegmentType: (type: SegmentType) => void
  setSegmentBalancingMode: (type: SegmentBalancingModeType) => void
  setAsns: (asns: api.switchns.Asn[]) => void
  setName: (name: string) => void
}

type SegmentWizardStore = PartialSegment & WizardMethods

const initialState: PartialSegment = {
  asns: [],
  balancings: {},
  capacityBalancingEnabled: false,
  devices: [],
  geolocation: null,
  mode: SegmentMode.NORMAL,
  name: null,
  parentUuid: null,
  protocol: undefined,
  segmentBalancingMode: SegmentBalancingVariant.PERCENTAGE,
  segmentType: SegmentVariant.ROOT,
  segmentUuid: null,
  siblingsCountryCodes: [],
  siblingsSubdivisionCodes: []
}

export const useSegmentWizardStore = create<SegmentWizardStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      clearWizard: () => set({ ...initialState }),
      setAsns: (asns: api.switchns.Asn[]) => {
        set({
          ...get(),
          asns: uniq(asns)
        })
      },
      setName: (name: string) => {
        set({
          ...get(),
          name: name.trim() || null
        })
      },
      setSegmentBalancingMode: (segmentBalancingMode: SegmentBalancingModeType) =>
        set(() => ({ segmentBalancingMode })),
      setSegmentType: (segmentType: SegmentType) => set(() => ({ segmentType })),
      setSegmentWizardState: (newState: Partial<SegmentWizardStore>) => {
        set({
          ...get(),
          ...newState
        })
      }
    }),
    {
      name: "segment-creation-wizard",
      storage: createJSONStorage(() => localStorage)
    }
  )
)
